"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { preload } from "react-dom";
import Button from "@/app/components/Button";
import Loading from "@/app/components/Loading";
import Gate from "@/app/components/Gate";
import { useConfirm } from "@/app/components/ConfirmDialog";
import { AccountView } from "@/lib/types";
import { Detail } from "./characterViews";
import PlayCharacter, { PartyMember } from "./PlayCharacter";
import PlayMap from "./PlayMap";
import PlayBattle from "./PlayBattle";
import PlayCraft from "./PlayCraft";
import PlayLog from "./PlayLog";
import PlayDictionary from "./PlayDictionary";
import PlayScenario from "./PlayScenario";
import PlayNews from "./PlayNews";
import PlayMessage from "./PlayMessage";
import PlayGuide, { Help } from "./PlayGuide";
import PlaySettings, { VIEW_DEFAULT, ViewSetting, readView, viewKey, writeView } from "./PlaySettings";
import { PLAY_CSS } from "./playStyle";

const WORLD = "Baunheim";
/** 画面に居る間に、フラグと散文を読み直す間隔 */
const REFRESH_MS = 15000;
/** ロード画面が最初の表示に要る絵を待つ上限 */
const GATE_MS = 8000;

type Tab = "dictionary" | "character" | "main" | "map" | "more";
type Sub = "log" | "scenario" | "settings" | "guide" | "message" | "credits";
type View = Tab | Sub | "battle" | "craft";

type Amplify = "off" | "interpolate" | "embellish" | "full_auto";

interface Flags {
  input_confirmed: boolean;
  in_progress: boolean;
  in_battle: boolean;
  in_craft: boolean;
  dead: boolean;
  /** 手直しの最中。入力シートを閉じる */
  in_rework?: boolean;
  /** 仲間入りしたキャラの作成待ち。入力シートを閉じる */
  in_recruit?: boolean;
  amplify: Amplify;
}

interface PlayState {
  slug: string;
  name: string;
  hp: { current: number; max: number } | null;
  pools: { label: string; color?: string | null; current: number; max: number }[];
  location: string | null;
  /** 共通時間 */
  time: string | null;
  /** 現在地の時間帯で直した現地時刻。時間帯が読めなければ null */
  local_time: string | null;
  flags: Flags;
  /** 戦闘が終わり、戦闘の後の進行を待っている */
  after_battle: boolean;
  /** 作製が終わり、作製の後の進行を待っている */
  after_craft?: boolean;
  /** 同行者。キャラ情報の一覧に並ぶ */
  companions?: { slug: string; ref: string; name: string; hp: { current: number; max: number } | null; pools: { label: string; color?: string | null; current: number; max: number }[] }[];
  /** 仲間入りしたキャラの作成待ち。キャラ情報の一覧に名だけが並ぶ */
  recruits?: { slug: string; name: string }[];
  draft: string;
  input: string;
  /** 最新の生成。場面で区切る。まだ無ければ null */
  story: { range: string; scenes: { no: string; continued: boolean; paragraphs: string[] }[] } | null;
  /** お知らせの未読の件数 */
  news_unread?: number;
  /** 次回の更新。next は次の受付の時刻で、kind が next のときだけ持つ */
  schedule?: { kind: "next" | "running" | "maintenance"; next: string | null };
  help: Help;
}

/** ヘッダに出す次回の更新の文 */
function scheduleLabel(schedule: PlayState["schedule"]): string {
  if (schedule?.kind === "running") return "更新中";
  const at = schedule?.kind === "next" && schedule.next ? new Date(schedule.next) : null;
  if (!at || Number.isNaN(at.getTime())) return "メンテナンス中";
  return `次回受付は ${at.getHours()}:${String(at.getMinutes()).padStart(2, "0")}`;
}

const AMPLIFY: { value: Amplify; label: string }[] = [
  { value: "off", label: "オフ" },
  { value: "interpolate", label: "オン" },
  { value: "embellish", label: "ブースト" },
  { value: "full_auto", label: "フルオート" },
];

const SUBS: { view: Sub; label: string; note: string }[] = [
  { view: "log", label: "ログ", note: "場面・時間・取引・戦闘・作製・成長・入力・報告の記録" },
  { view: "scenario", label: "脚本設定", note: "物語の味付け・執筆規定・禁句" },
  { view: "settings", label: "設定", note: "文字サイズ・表示の設定" },
  { view: "guide", label: "遊び方ガイド", note: "遊びの流れと各画面の説明" },
  { view: "message", label: "開発へメッセージ", note: "FB 報告・修正依頼・手直し依頼" },
  { view: "credits", label: "クレジット", note: "開発者の詳細" },
];

const SHEET_TABS: Tab[] = ["dictionary", "character", "main", "map"];

/** 暦。1 か月 28 日・1 年 13 か月。1 日目は 9 月 1 日 */
const MONTH_DAYS = 28;
const YEAR_MONTHS = 13;
const START_MONTH = 9;

/** `Day001-07` を 9 月 1 日 7 時 の形にする */
function timeLabel(time: string | null): string {
  const match = time ? /^Day(\d+)-(\d+)$/.exec(time) : null;
  if (!match) return time ?? "—";
  const days = (START_MONTH - 1) * MONTH_DAYS + Number(match[1]) - 1;
  const month = Math.floor(days / MONTH_DAYS) % YEAR_MONTHS + 1;
  const day = days % MONTH_DAYS + 1;
  return `${month} 月 ${day} 日 ${Number(match[2])} 時`;
}

/** フッタのアイコン。絵は public/ui の icon-{名}.webp */
function Icon({ name }: { name: Tab }) {
  return <img className="pl-foot-icon" src={`/ui/icon-${name}.webp`} alt="" width={128} height={128} />;
}


function Placeholder() {
  return (
    <div>
      <div className="pl-empty">
        <strong>この画面は未実装です。</strong>
        設計が決まり次第、ここに中身が入ります。
      </div>
    </div>
  );
}

/** クレジットの仮の画面 */
function Credits() {
  return (
    <div>
      <figure className="pl-credit">
        <img src="/credits/min-usi.jpg" alt="ミン牛" />
        <figcaption>
          <span className="pl-credit-line">This is MinUsi</span>
          <span className="pl-credit-script">thank you very much</span>
        </figcaption>
      </figure>
    </div>
  );
}

/**
 * Baunheim の、キャラを選んだ後の画面。
 * ヘッダ・中央・フッタで組み、フッタの左 4 つの画面では入力シートのバーを出す。
 * 中身の定めは Baunheim の WebUI の詳細設計書が持つ
 */
const PRELOAD_IMAGES = [
  "/Baunheim/ui/crest-baunheim.webp",
  ...["news", "menu", "dictionary", "character", "main", "map", "more"].map((name) => `/ui/icon-${name}.webp`),
  "/ui/tex-leather.webp",
  "/ui/tex-wood.webp",
];

/** ほかの画面で使う絵。進行の画面を開いたら、切り替える前に裏で取っておく。先に使う順に並べる */
const WARM_IMAGES = [
  ...["str", "dex", "agi", "def", "int", "mnd", "fth", "cha", "per", "lck"].map((key) => `/Baunheim/ui/stat-${key}.webp`),
  ...["log", "scenario", "settings", "guide", "message", "credits", "battle", "craft", "loading"].map((name) => `/ui/icon-${name}.webp`),
  ...["frame-card", "frame-dialog", "frame-field", "frame-round", "frame-banner", "btn-tab", "btn-round", "deco-corner", "deco-divider", "deco-gauge", "deco-seal", "tex-paper-light"].map((name) => `/ui/${name}.webp`),
  ...["washi", "parchment", "slate", "night", "digital", "cute", "elegant", "forest"].map((name) => `/ui/tex-look-${name}.webp`),
];

/** 取っておいた絵。手放すとブラウザが捨てるので、ここに持ち続ける */
const warmed: HTMLImageElement[] = [];

function warmImages() {
  if (warmed.length > 0) return;
  for (const src of WARM_IMAGES) {
    const image = new Image();
    image.decoding = "async";
    image.src = src;
    image.decode().catch(() => {});
    warmed.push(image);
  }
}

/**
 * 行動の入力欄と、その下の文字数・保存の印。打った文字はこの部品の中だけで持ち、打つたびに組み直すのはこの部品だけにする。
 * 打った文字は onChange で親の写しへ渡す。親が書きかけを入れ替えるときは key を替えて組み直し、initial から始める
 */
function DraftField({
  initial,
  fullAuto,
  look,
  saving,
  dirty,
  disabled,
  onChange,
}: {
  initial: string;
  fullAuto: boolean;
  look: string;
  saving: boolean;
  dirty: boolean;
  disabled: boolean;
  onChange: (text: string) => void;
}) {
  const [text, setText] = useState(initial);
  return (
    <>
      {fullAuto ? (
        <div className={`pl-textarea pl-textarea-off pl-input-look pl-look-${look}`}>フルオートでは入力を使いません。</div>
      ) : (
        <textarea
          className={`pl-textarea pl-input-look pl-look-${look}`}
          value={text}
          onChange={(event) => {
            setText(event.target.value);
            onChange(event.target.value);
          }}
          placeholder="例：市場で薬草を買ってから、洞窟へ向かう"
          disabled={disabled}
        />
      )}
      <div className="pl-sheet-row">
        <span className="pl-sheet-label">おまかせオプション</span>
        <span className="pl-sheet-save">
          {fullAuto ? "" : `${text.length} 文字`}
          {!fullAuto && (saving || dirty || text) ? " ・ " : ""}
          {saving ? "保存しています" : dirty ? "未保存" : text ? "保存済み" : ""}
        </span>
      </div>
    </>
  );
}

export default function Play({
  region,
  slug,
  account,
  onExit,
  onLogout,
}: {
  region: string;
  slug: string;
  account: AccountView;
  onExit: () => void;
  onLogout: () => void;
}) {
  // ヘッダとフッタの絵を、画面を組む前に取りに行かせる
  for (const src of PRELOAD_IMAGES) preload(src, { as: "image", type: "image/webp" });

  useEffect(warmImages, []);

  const query = `world=${WORLD}&region=${encodeURIComponent(region)}&slug=${encodeURIComponent(slug)}`;
  const [state, setState] = useState<PlayState | null>(null);
  const [view, setView] = useState<View>("main");
  const [headMenu, setHeadMenu] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [message, setMessage] = useState("");

  const [sheetOpen, setSheetOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [newsOpen, setNewsOpen] = useState(false);
  // 書きかけの中身は写しに持ち、画面は組み直さない。画面が要るのは、空かどうかと入力欄を組み直す番号だけ
  const draftRef = useRef("");
  const [hasDraft, setHasDraft] = useState(false);
  const [draftKey, setDraftKey] = useState(0);
  const [amplify, setAmplify] = useState<Amplify>("off");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const seeded = useRef(false);
  const wasConfirmed = useRef(false);

  const [detail, setDetail] = useState<Detail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailMessage, setDetailMessage] = useState("");
  // キャラ情報で開いているキャラ。一覧を出している間は null。替わるたびにロード画面を挟む
  const [openedChar, setOpenedChar] = useState<string | null>(null);
  // 読みの中から今の中身を見るための写しと、キャラ情報の読みの通し番号
  const stateRef = useRef<PlayState | null>(null);
  const detailRef = useRef<Detail | null>(null);
  const detailSeq = useRef(0);
  stateRef.current = state;
  detailRef.current = detail;

  // 最初の表示に要る絵が揃ったか。揃うまでロード画面を出す
  const [imagesReady, setImagesReady] = useState(false);

  const [ask, confirmDialog] = useConfirm();

  // メインの画面の文字サイズと表示。キャラごとにブラウザへ覚える
  const settingKey = viewKey(account.id, WORLD, region, slug);
  const [setting, setSetting] = useState<ViewSetting>(VIEW_DEFAULT);
  useEffect(() => {
    setSetting(readView(settingKey));
  }, [settingKey]);
  const changeSetting = (next: ViewSetting) => {
    setSetting(next);
    writeView(settingKey, next);
  };

  /** フラグ・散文・キャラの要約を読む。書きかけは最初の 1 回だけ画面へ入れる */
  const refresh = useCallback(async () => {
    try {
      const response = await fetch(`/api/play/state?${query}`);
      const body = await response.json();
      if (!response.ok) {
        setMessage(body.error ?? "画面を読めません");
        return;
      }
      const next = body as PlayState;
      setState(next);
      if (!seeded.current) {
        seeded.current = true;
        draftRef.current = next.draft;
        setHasDraft(next.draft.trim() !== "");
        setDraftKey((key) => key + 1);
        setAmplify(next.flags.amplify);
      } else if (wasConfirmed.current && !next.flags.input_confirmed && next.draft === "") {
        // 進行が終わると書きかけが空になる。開いたままの画面の書きかけも空にする
        draftRef.current = "";
        setHasDraft(false);
        setDraftKey((key) => key + 1);
        setDirty(false);
      }
      wasConfirmed.current = next.flags.input_confirmed;
    } catch {
      setMessage("サーバーに繋がりません");
    }
  }, [query]);

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, REFRESH_MS);
    return () => clearInterval(timer);
  }, [refresh]);

  // 裏に回ったタブと眠ったスマホは、間隔の読み直しが止まる。画面へ戻ったらすぐ読み直す
  useEffect(() => {
    const wake = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", wake);
    window.addEventListener("pageshow", wake);
    window.addEventListener("online", wake);
    return () => {
      document.removeEventListener("visibilitychange", wake);
      window.removeEventListener("pageshow", wake);
      window.removeEventListener("online", wake);
    };
  }, [refresh]);

  // 最初の表示に要る絵を取り終えるまで待つ。取れない絵が在っても GATE_MS で先へ進む
  useEffect(() => {
    let alive = true;
    const done = () => {
      if (alive) setImagesReady(true);
    };
    const timer = setTimeout(done, GATE_MS);
    Promise.allSettled(
      PRELOAD_IMAGES.map((src) => {
        const image = new Image();
        image.src = src;
        return image.decode();
      })
    ).then(done);
    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, []);

  /**
   * 確定とその取り消し・進行・戦闘・作製・手直し・仲間入り・死亡で変わる所を繋いだ印。
   * 変わったら、開いている画面（キャラ情報・辞書・マップの現在地・ログ）の中身を読み直す。
   * 装備を替えられるかは確定で変わるので、確定のフラグも繋ぐ
   */
  const version = state
    ? [
        state.time,
        state.location,
        state.story?.range ?? "",
        state.hp?.current ?? "",
        state.flags.input_confirmed,
        state.flags.dead,
        state.flags.in_progress,
        state.flags.in_battle,
        state.flags.in_craft,
        state.flags.in_rework === true,
        state.flags.in_recruit === true,
        state.after_battle,
        state.after_craft === true,
        (state.companions ?? []).map((c) => c.slug).join(","),
        (state.recruits ?? []).map((c) => c.slug).join(","),
      ].join("|")
    : "";

  const post = useCallback(
    async (op: "draft" | "confirm" | "cancel", body: Record<string, unknown>) => {
      const response = await fetch(`/api/play/${op}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ world: WORLD, region, slug, ...body }),
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error ?? "送れません");
      return result as { ok: boolean; flags: Flags };
    },
    [region, slug]
  );

  const saveDraft = useCallback(
    async (text: string, chosen: Amplify) => {
      setSaving(true);
      try {
        const result = await post("draft", { text, amplify: chosen });
        setState((previous) => (previous ? { ...previous, draft: text, flags: result.flags } : previous));
        setDirty(false);
      } catch (error) {
        setMessage(error instanceof Error ? error.message : "書きかけを保存できません");
      } finally {
        setSaving(false);
      }
    },
    [post]
  );

  /** シートを畳む。書きかけはここで保存する */
  const closeSheet = () => {
    setSheetOpen(false);
    if (dirty) saveDraft(draftRef.current, amplify);
  };

  /** 入力欄が打たれたとき。画面を組み直すのは、空かどうかと未保存の印が替わったときだけ */
  const changeDraft = useCallback((text: string) => {
    draftRef.current = text;
    setHasDraft(text.trim() !== "");
    setDirty(true);
  }, []);

  const changeAmplify = (chosen: Amplify) => {
    setAmplify(chosen);
    saveDraft(draftRef.current, chosen);
  };

  /** フルオートは入力欄を使わず、空の入力で確定する */
  const fullAuto = amplify === "full_auto";

  const confirm = async () => {
    if (sending || (!fullAuto && draftRef.current.trim() === "")) return;
    const question = fullAuto
      ? "フルオートで進行を確定します。入力は使いません。\n進行が始まるまでは取り消せます。"
      : "この内容で行動を確定します。\n進行が始まるまでは取り消せます。";
    if (!(await ask(question, { ok: "確定する" }))) return;
    setSending(true);
    setMessage("");
    try {
      const text = fullAuto ? "" : draftRef.current;
      const result = await post("confirm", { text, amplify });
      setState((previous) => (previous ? { ...previous, input: text, flags: result.flags } : previous));
      setDirty(false);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "確定できません");
    } finally {
      setSending(false);
    }
  };

  const cancel = async () => {
    if (sending) return;
    if (!(await ask("確定を取り消します。書いた内容は残ります。", { ok: "取り消す", danger: true }))) return;
    setSending(true);
    setMessage("");
    try {
      const result = await post("cancel", {});
      setState((previous) => (previous ? { ...previous, flags: result.flags } : previous));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "取り消せません");
    } finally {
      setSending(false);
    }
  };

  /**
   * キャラ情報の中身を読む。quiet は、出ている中身を残したまま裏で読み直す。
   * 後から始めた読みだけを画面へ入れる
   */
  const loadDetail = useCallback(
    (target: string, quiet: boolean) => {
      const seq = ++detailSeq.current;
      if (!quiet) {
        setDetail(null);
        setDetailLoading(true);
        setDetailMessage("");
      }
      // 同行者は持ち主の PC を添えて読む
      const now = stateRef.current;
      const owner = now && target !== now.slug && (now.companions ?? []).some((c) => c.slug === target) ? now.slug : null;
      fetch(`/api/character/detail?world=${WORLD}&region=${encodeURIComponent(region)}&slug=${encodeURIComponent(target)}${owner ? `&owner=${encodeURIComponent(owner)}` : ""}`)
        .then(async (response) => {
          const body = await response.json();
          if (seq !== detailSeq.current) return;
          if (response.ok) setDetail(body as Detail);
          else if (!quiet) setDetailMessage(body.error ?? "キャラクターを読めません");
        })
        .catch(() => {
          if (seq === detailSeq.current && !quiet) setDetailMessage("サーバーに繋がりません");
        })
        .finally(() => {
          if (seq === detailSeq.current) setDetailLoading(false);
        });
    },
    [region]
  );

  /** 一覧でキャラを押したとき。押すたびに読み直す。同じキャラは、出ている中身を残して裏で読む */
  const openDetail = useCallback((target: string) => loadDetail(target, detailRef.current?.slug === target), [loadDetail]);

  // 進行・戦闘・作製・手直し・仲間入りが動いたら、開いているキャラ情報の中身を読み直す
  useEffect(() => {
    if (version !== "" && detailRef.current) loadDetail(detailRef.current.slug, true);
  }, [version, loadDetail]);

  /** 戦闘に持っていくスキルを決める。決まったら開いている中身の印を揃える */
  const saveLoadout = useCallback(
    async (target: string, loadout: string[]): Promise<string | null> => {
      const owner = state && target !== state.slug && (state.companions ?? []).some((c) => c.slug === target) ? state.slug : null;
      try {
        const response = await fetch("/api/character/loadout", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          // 同行者は持ち主の PC を添えて選ぶ
          body: JSON.stringify({ world: WORLD, region, slug: target, loadout, ...(owner ? { owner } : {}) }),
        });
        const body = await response.json();
        if (!response.ok) return body.error ?? "決められません";
        setDetail((now) => (now && now.slug === target ? { ...now, skills: now.skills.map((s) => ({ ...s, loadout: loadout.includes(s.slug) })) } : now));
        return null;
      } catch {
        return "サーバーに繋がりません";
      }
    },
    [region, state]
  );

  /** 振り分けポイントを振る。決まったら開いている中身を振った後の値に替える */
  const saveGrowth = useCallback(
    async (target: string, spend: Record<string, number>): Promise<string | null> => {
      try {
        const response = await fetch("/api/character/growth", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ world: WORLD, region, slug: target, spend }),
        });
        const body = await response.json();
        if (!response.ok) return body.error ?? "振れません";
        setDetail(body as Detail);
        refresh();
        return null;
      } catch {
        return "サーバーに繋がりません";
      }
    },
    [region, refresh]
  );

  /** 装備の付け替えと持ち物の破棄を決める。決まったら開いている中身を、予約を添えた中身に替える */
  const saveEquip = useCallback(
    async (target: string, action: "on" | "off" | "drop" | "keep" | "reset", id?: string, where?: { part: string; layer: number }): Promise<string | null> => {
      const owner = state && target !== state.slug && (state.companions ?? []).some((c) => c.slug === target) ? state.slug : null;
      try {
        const response = await fetch("/api/character/equip", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          // 同行者は持ち主の PC を添えて替える
          body: JSON.stringify({ world: WORLD, region, slug: target, action, ...(id ? { id } : {}), ...(where ? { part: where.part, layer: where.layer } : {}), ...(owner ? { owner } : {}) }),
        });
        const body = await response.json();
        if (!response.ok) return body.error ?? "替えられません";
        setDetail((now) => (now && now.slug === target ? (body as Detail) : now));
        return null;
      } catch {
        return "サーバーに繋がりません";
      }
    },
    [region, state]
  );

  const flags = state?.flags ?? null;
  // 戦闘中・クラフト中はバーを出さない。その間は確定が起きない
  // 死亡したキャラはバーがゲームオーバーの表示になり、開かない。戦闘と作製のボタンも出さない
  const dead = flags?.dead === true;
  const inBattle = !dead && flags?.in_battle === true;
  const inCraft = !dead && flags?.in_craft === true;
  const showBar = SHEET_TABS.includes(view as Tab) && !inBattle && !inCraft;
  // 戦闘中は入力シートのバーの代わりに、戦闘の一時画面へ入るボタンを出す
  const showBattleBar = SHEET_TABS.includes(view as Tab) && inBattle;
  // 作製中も同じ場所に、作製の一時画面へ入るボタンを出す
  const showCraftBar = SHEET_TABS.includes(view as Tab) && !inBattle && inCraft;
  const afterCraft = state?.after_craft === true;
  const reworking = flags?.in_rework === true;
  const recruiting = flags?.in_recruit === true;
  const busy = flags?.in_progress === true || reworking || recruiting;
  const confirmed = flags?.input_confirmed === true;
  const afterBattle = state?.after_battle === true;
  // 戦闘の後の進行を待つ間は、結末が次の進行に入るのでシートを開かない
  const sheetVisible = showBar && sheetOpen && !busy && !dead && !afterBattle;
  // バーが開かない間は、開く印の ▲ を出さない
  const barLocked = dead || busy || afterBattle || state === null;

  const barLabel = dead ? (
    "ゲームオーバー"
  ) : busy ? (
    <span className="pl-bar-busy">
      <span className="pl-pulse" />
      {reworking ? "キャラを手直ししています" : flags?.in_progress === true ? "物語を紡いでいます" : "味方キャラ作成中につき停止中"}
    </span>
  ) : afterBattle ? (
    "戦闘の結末を物語にします"
  ) : afterCraft ? (
    "作製の結末を物語にします"
  ) : confirmed ? (
    <span>
      <img className="pl-seal" src="/ui/deco-seal.webp" alt="" width={192} height={192} />
      行動を確定しました
    </span>
  ) : fullAuto ? (
    "フルオートで進める"
  ) : hasDraft ? (
    "書きかけの行動があります"
  ) : (
    "行動を入力する"
  );

  const barState = dead ? "" : reworking ? "手直し中" : flags?.in_progress === true ? "進行中" : recruiting ? "停止中" : afterBattle || afterCraft ? "次の進行を待つ" : confirmed ? "取り消せます" : "";

  const party: PartyMember[] = state
    ? [
        { slug: state.slug, name: state.name, hp: state.hp, pools: state.pools },
        ...(state.companions ?? []).map((c) => ({ slug: c.slug, name: c.name, hp: c.hp, pools: c.pools, owner: state.slug })),
        ...(state.recruits ?? []).map((c) => ({ slug: c.slug, name: c.name, hp: null, pools: [], owner: state.slug, recruiting: true })),
      ]
    : [];

  const go = (next: View) => {
    setView(next);
    // キャラ情報は、開くたびに一覧から始める
    setOpenedChar(null);
    setHeadMenu(false);
    if (!SHEET_TABS.includes(next as Tab) && sheetOpen) closeSheet();
  };

  const scenes = (state?.story?.scenes ?? []).filter((scene) => scene.paragraphs.length > 0);
  const newsUnread = state?.news_unread ?? 0;

  // ロード画面。最初の中身と絵が揃うまで、画面の枠を出さずに待つ
  if (state === null || !imagesReady) {
    return (
      <div className="pl-root">
        <style dangerouslySetInnerHTML={{ __html: PLAY_CSS }} />
        <div className="pl-app pl-gate">
          {message ? (
            <>
              <p className="pl-message">{message}</p>
              <Button inline onClick={onExit}>
                キャラ選択画面へ戻る
              </Button>
            </>
          ) : (
            <Loading label="物語を開いています" />
          )}
        </div>
      </div>
    );
  }

  // 戦闘と作製の一時画面も、中身が出せるまでロード画面を挟む
  if (view === "battle") {
    return (
      <Gate key="battle" variant="screen" label="戦闘を読んでいます">
        <PlayBattle
          region={region}
          slug={slug}
          onExit={() => {
            setView("main");
            refresh();
          }}
        />
      </Gate>
    );
  }

  if (view === "craft") {
    return (
      <Gate key="craft" variant="screen" label="作製を読んでいます">
        <PlayCraft
          region={region}
          slug={slug}
          onExit={() => {
            setView("main");
            refresh();
          }}
        />
      </Gate>
    );
  }

  let center: React.ReactNode;
  if (view === "main") {
    center = (
      <div>
        <div className="pl-meta">
          <span>
            <b>{state?.name ?? slug}</b>
          </span>
          <span>
            <b>{timeLabel(state?.local_time ?? state?.time ?? null)}</b>
          </span>
        </div>
        {state === null ? (
          <Loading />
        ) : scenes.length > 0 ? (
          <div className="pl-story">
            {scenes.map((scene, sceneIndex) => (
              <section key={scene.no} className="pl-scene">
                {sceneIndex > 0 ? <hr className="pl-scene-rule" /> : null}
                {scene.paragraphs.map((line, index) => (
                  <p key={index}>{line}</p>
                ))}
              </section>
            ))}
          </div>
        ) : (
          <div className="pl-empty">
            <strong>まだ物語は始まっていません。</strong>
            下のバーから最初の行動を書いて、確定してください。進行が終わると、ここに散文が並びます。
          </div>
        )}
      </div>
    );
  } else if (view === "character") {
    center = (
      <PlayCharacter
        region={region}
        party={party}
        detail={detail}
        loading={detailLoading}
        message={detailMessage}
        opened={openedChar}
        onOpened={setOpenedChar}
        onOpen={openDetail}
        onLoadout={saveLoadout}
        onGrowth={saveGrowth}
        onEquip={saveEquip}
      />
    );
  } else if (view === "dictionary") {
    center = <PlayDictionary region={region} slug={slug} version={version} />;
  } else if (view === "map") {
    center = <PlayMap region={region} slug={slug} location={state?.location ?? null} version={version} />;
  } else if (view === "more") {
    center = (
      <div>
        <div className="pl-title">
          <h2>その他</h2>
        </div>
        <div className="pl-list">
          {SUBS.map((one) => (
            <button key={one.view} type="button" className="pl-list-btn" onClick={() => go(one.view)}>
              <img className="pl-list-ico" src={`/ui/icon-${one.view}.webp`} alt="" width={128} height={128} />
              <span>
                {one.label}
                <br />
                <small>{one.note}</small>
              </span>
            </button>
          ))}
        </div>
      </div>
    );
  } else if (view === "scenario") {
    // ヘッダの説明がタブで替わるから、画面が自分でヘッダを出す
    center = <PlayScenario region={region} slug={slug} onBack={() => go("more")} />;
  } else if (view === "message") {
    // ヘッダの説明が種類で替わるから、画面が自分でヘッダを出す
    center = <PlayMessage region={region} slug={slug} targets={party.filter((one) => !one.recruiting).map((one) => ({ slug: one.slug, name: one.name, owner: one.owner }))} onBack={() => go("more")} />;
  } else {
    const sub = SUBS.find((one) => one.view === view);
    center = (
      <div>
        {/* その他から開いた画面のヘッダ。1 行目は左に題、右にその他へ戻るボタン。2 行目は細かい説明。上に留まる */}
        <div className="pl-title">
          <h2>{sub?.label ?? ""}</h2>
          {view === "log" ? <span>時刻は共通時間</span> : null}
          {view === "settings" ? <p className="pl-title-lead">この設定は、このキャラとこのブラウザに覚えます。メインの画面の散文と、ログの場面の散文に効きます。</p> : null}
          <button type="button" className="pl-back" onClick={() => go("more")}>
            戻る
          </button>
        </div>
        {view === "guide" && state ? (
          <PlayGuide help={state.help} />
        ) : view === "log" ? (
          <PlayLog region={region} slug={slug} view={setting} version={version} />
        ) : view === "settings" ? (
          <PlaySettings value={setting} onChange={changeSetting} />
        ) : view === "credits" ? (
          <Credits />
        ) : (
          <Placeholder />
        )}
      </div>
    );
  }

  const tabs: { tab: Tab; label: string }[] = [
    { tab: "dictionary", label: "辞書" },
    { tab: "character", label: "キャラ情報" },
    { tab: "main", label: "メイン" },
    { tab: "map", label: "マップ" },
    { tab: "more", label: "その他" },
  ];
  const activeTab: Tab = SHEET_TABS.includes(view as Tab) ? (view as Tab) : "more";

  return (
    <div className="pl-root">
      <style dangerouslySetInnerHTML={{ __html: PLAY_CSS }} />
      <div className={dead ? "pl-app pl-app-dead" : "pl-app"}>
        <header className="pl-head">
          <div className="pl-brand">
            <img className="pl-brand-crest" src="/Baunheim/ui/crest-baunheim.webp" alt="" width={192} height={226} />
            <div className="pl-brand-text">
              <span className="pl-brand-name">Baunheim</span>
              <span className={`pl-brand-next pl-brand-next-${state.schedule?.kind ?? "maintenance"}`}>{scheduleLabel(state.schedule)}</span>
            </div>
          </div>
          <div className="pl-head-actions">
            <button
              type="button"
              className={newsOpen ? "pl-head-btn pl-head-btn-on" : newsUnread > 0 ? "pl-head-btn pl-head-btn-new" : "pl-head-btn"}
              aria-label={newsUnread > 0 ? `お知らせ。未読 ${newsUnread} 件` : "お知らせ"}
              onClick={() => {
                setHeadMenu(false);
                setNewsOpen(true);
              }}
            >
              <img className="pl-head-ico" src="/ui/icon-news.webp" alt="" width={128} height={128} />
              {newsUnread > 0 ? <i className="pl-dot" /> : null}
            </button>
            <button
              type="button"
              className={headMenu ? "pl-head-btn pl-head-btn-on" : "pl-head-btn"}
              aria-label="メニュー"
              aria-expanded={headMenu}
              onClick={() => setHeadMenu((open) => !open)}
            >
              <img className="pl-head-ico" src="/ui/icon-menu.webp" alt="" width={128} height={128} />
            </button>
          </div>
          {headMenu ? (
            <div className="pl-head-menu" role="menu">
              <button type="button" role="menuitem" onClick={onExit}>
                キャラ選択画面へ戻る
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setHeadMenu(false);
                  setAccountOpen(true);
                }}
              >
                アカウント情報
              </button>
              <button type="button" role="menuitem" onClick={onLogout}>
                ログアウト
              </button>
            </div>
          ) : null}
        </header>
        {headMenu ? <div className="pl-scrim" onClick={() => setHeadMenu(false)} /> : null}

        {/* 下のボタンとその他の一覧で画面を切り替えるたびに、中身が出せるまでロード画面を挟む。キャラ情報は、開くキャラを替えるたびにも挟む */}
        <Gate key={view === "character" ? `character:${openedChar ?? ""}` : view} variant="area">
          <main className={view === "main" ? `pl-main pl-look-${setting.look} pl-size-${setting.size}` : "pl-main"}>
            {center}
            {message ? <p className="pl-message">{message}</p> : null}
          </main>
        </Gate>

        {showBar ? (
          <div className={sheetVisible ? "pl-sheet pl-sheet-open" : "pl-sheet"} aria-hidden={!sheetVisible}>
            <div className="pl-sheet-body">
              {confirmed ? (
                <>
                  <div className="pl-sheet-row">
                    <span className="pl-sheet-label">{flags?.amplify === "full_auto" ? "確定した内容" : "確定した文章"}</span>
                    <span className="pl-sheet-save">おまかせ：{AMPLIFY.find((one) => one.value === (flags?.amplify ?? "off"))?.label}</span>
                  </div>
                  <div className={`pl-confirmed pl-input-look pl-look-${setting.look}`}>{flags?.amplify === "full_auto" ? "フルオートでは入力を使いません。" : state?.input}</div>
                  <p className="pl-note">進行が始まるまでは取り消せます。</p>
                </>
              ) : (
                <>
                  <div className="pl-sheet-row">
                    <span className="pl-sheet-label">物語の次の一手を記入</span>
                    <button type="button" className="pl-help-btn" aria-label="書き方のヘルプ" onClick={() => setHelpOpen(true)}>
                      ?
                    </button>
                  </div>
                  <DraftField
                    key={draftKey}
                    initial={draftRef.current}
                    fullAuto={fullAuto}
                    look={setting.look}
                    saving={saving}
                    dirty={dirty}
                    disabled={sending || state === null}
                    onChange={changeDraft}
                  />
                  <div className="pl-seg" role="radiogroup" aria-label="おまかせオプション">
                    {AMPLIFY.map((one) => (
                      <button
                        key={one.value}
                        type="button"
                        role="radio"
                        aria-checked={amplify === one.value}
                        className={amplify === one.value ? "pl-seg-on" : ""}
                        onClick={() => changeAmplify(one.value)}
                        disabled={sending || state === null}
                      >
                        {one.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            <div className="pl-sheet-actions">
              {afterBattle || afterCraft ? null : confirmed ? (
                <Button variant="danger" onClick={cancel} disabled={sending}>
                  {sending ? "送っています" : "確定を取り消す"}
                </Button>
              ) : (
                <Button variant="primary" onClick={confirm} disabled={sending || state === null || (!fullAuto && !hasDraft)}>
                  {sending ? "送っています" : "確定"}
                </Button>
              )}
              <Button inline onClick={closeSheet} disabled={sending}>
                閉じる
              </Button>
            </div>
          </div>
        ) : null}

        {showBattleBar ? (
          <button type="button" className="pl-bar pl-bar-battle" onClick={() => go("battle")}>
            <span>
              <img className="pl-bar-ico" src="/ui/icon-battle.webp" alt="" width={128} height={128} />
              戦闘中
            </span>
            <span>
              <span className="pl-bar-state">戦闘へ</span>
              <span className="pl-bar-arrow">▶</span>
            </span>
          </button>
        ) : null}

        {showCraftBar ? (
          <button type="button" className="pl-bar pl-bar-craft" onClick={() => go("craft")}>
            <span>
              <img className="pl-bar-ico" src="/ui/icon-craft.webp" alt="" width={128} height={128} />
              作製中
            </span>
            <span>
              <span className="pl-bar-state">作製へ</span>
              <span className="pl-bar-arrow">▶</span>
            </span>
          </button>
        ) : null}

        {showBar ? (
          <button
            type="button"
            className={dead ? "pl-bar pl-bar-dead" : sheetVisible ? "pl-bar pl-bar-open" : "pl-bar"}
            onClick={() => (sheetOpen ? closeSheet() : setSheetOpen(true))}
            disabled={barLocked}
            aria-expanded={sheetVisible}
          >
            <span>{barLabel}</span>
            <span>
              <span className="pl-bar-state">{barState}</span>
              {barLocked ? null : <span className="pl-bar-arrow">▲</span>}
            </span>
          </button>
        ) : null}

        <nav className="pl-foot">
          {tabs.map((one) => (
            <button
              key={one.tab}
              type="button"
              className={activeTab === one.tab ? "pl-foot-btn pl-foot-btn-on" : "pl-foot-btn"}
              aria-current={activeTab === one.tab}
              onClick={() => go(one.tab)}
            >
              <Icon name={one.tab} />
              {one.label}
            </button>
          ))}
        </nav>

        {newsOpen ? (
          <PlayNews region={region} slug={slug} onUnread={(count) => setState((previous) => (previous ? { ...previous, news_unread: count } : previous))} onClose={() => setNewsOpen(false)} />
        ) : null}

        {helpOpen && state ? (
          <div className="pl-modal-back pl-modal-back-center" onClick={() => setHelpOpen(false)}>
            <div className="pl-modal pl-modal-center pl-help" role="dialog" aria-label="書き方のヘルプ" onClick={(event) => event.stopPropagation()}>
              <div className="pl-modal-head">
                <h3>書き方のヘルプ</h3>
                <button type="button" className="pl-modal-x" aria-label="閉じる" onClick={() => setHelpOpen(false)}>
                  ×
                </button>
              </div>
              <div className="pl-modal-body">
                <h4>書き方</h4>
                <ul>
                  {state.help.how.map(([line, example]) => (
                    <li key={line}>
                      {line}
                      {example ? <small>{example}</small> : null}
                    </li>
                  ))}
                </ul>
                <h4>注意点</h4>
                <ul>
                  {state.help.notes.map((line) => (
                    <li key={line}>{line}</li>
                  ))}
                </ul>
                <h4>おまかせオプション</h4>
                <ul>
                  {state.help.options.map(([label, line]) => (
                    <li key={label}>
                      <b>{label}</b>　{line}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ) : null}

        {accountOpen ? (
          <div className="pl-modal-back" onClick={() => setAccountOpen(false)}>
            <div className="pl-modal" onClick={(event) => event.stopPropagation()}>
              <h3>アカウント情報</h3>
              <div className="pl-kv">
                <span>ユーザー名</span>
                <b>{account.displayName}</b>
              </div>
              <div className="pl-kv">
                <span>アカウント id</span>
                <b>{account.id}</b>
              </div>
              <div className="pl-kv">
                <span>遊んでいるキャラ</span>
                <b>
                  {state?.name ?? slug}（{region}）
                </b>
              </div>
              <div className="pl-kv">
                <span>AP</span>
                <b>{account.ap.current}</b>
              </div>
              <div className="actions">
                <Button onClick={() => setAccountOpen(false)}>閉じる</Button>
              </div>
            </div>
          </div>
        ) : null}

        {confirmDialog}
      </div>
    </div>
  );
}
